import { ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Age, ClientName, StatusPill, daysSince } from "@/components/dashboard/queueShared";

const evaluationAge = (item) => {
  if (item.overdue) return { tone: "red", label: `Overdue · ${item.days_late} d` };
  if (item.days_until_due === 0) return { tone: "amber", label: "Due today" };
  return { tone: "amber", label: `Due in ${item.days_until_due} d` };
};

const buildCategories = (attention, coverage, clientScoped) => [
  {
    id: "evaluation",
    testId: "attention-evaluations",
    title: "Evaluations due",
    rows: attention?.evaluations_attention || [],
    columns: [
      { key: "client", label: "Client", render: (row) => <ClientName name={row.client_name} /> },
      {
        key: "need",
        label: "Evaluation",
        render: (row) => (
          <span className="ops-cell-need">
            {row.student_name}
            <small> · {row.provider_name || "Unassigned"}</small>
          </span>
        ),
      },
      {
        key: "age",
        label: "Due",
        num: true,
        render: (row) => {
          const age = evaluationAge(row);
          return (
            <span className={`ops-age is-${age.tone}`} title={row.due_date}>
              {age.label}
            </span>
          );
        },
      },
      { key: "status", label: "Status", render: (row) => <StatusPill status={row.status} /> },
    ],
    action: (row) => ({ to: `/evaluation-review?focus=${row.id}`, label: "Review" }),
  },
  {
    id: "submission",
    testId: "attention-submissions",
    title: "Awaiting approval",
    note: clientScoped ? "Shown across all clients" : null,
    rows: attention?.pending_submissions || [],
    columns: [
      { key: "client", label: "Provider", render: (row) => <span className="ops-cell-client">{row.provider_name}</span> },
      {
        key: "need",
        label: "Submission",
        render: (row) => (
          <span className="ops-cell-need">
            {row.submission_type}
            {row.detail ? <small> · {row.detail}</small> : null}
          </span>
        ),
      },
      { key: "age", label: "Waiting", num: true, render: (row) => <Age days={daysSince(row.submitted_at)} /> },
      { key: "status", label: "Status", render: (row) => <StatusPill status={row.status} /> },
    ],
    action: () => ({ to: "/providers", label: "Approve" }),
  },
  {
    id: "unlinked",
    testId: "attention-unlinked",
    title: "Open positions without a client",
    count: Number(coverage?.unlinked_open_positions || 0),
    callout: (count) => `${count} open ${count === 1 ? "position has" : "positions have"} no client linked, so they cannot be confirmed or invoiced.`,
    link: { to: "/master-staffing-board?card=attention", label: "Link clients" },
  },
];

const categoryCount = (category) => (category.rows ? category.rows.length : category.count);

const QueueTable = ({ category }) => (
  <table className="ops-table ops-queue-table">
    <thead>
      <tr>
        {category.columns.map((column) => (
          <th key={column.key} scope="col" className={column.num ? "is-num" : ""}>
            {column.label}
          </th>
        ))}
        <th scope="col" className="is-action">
          Next action
        </th>
      </tr>
    </thead>
    <tbody>
      {category.rows.map((row) => {
        const action = category.action(row);
        const testId = `attention-${category.id}-${row.id}`;
        return (
          <tr key={row.id} data-testid={testId}>
            {category.columns.map((column) => (
              <td key={column.key} data-label={column.label} className={column.num ? "is-num" : ""}>
                {column.render(row)}
              </td>
            ))}
            <td data-label="Next action" className="is-action">
              <Link className="ops-row-action" to={action.to} data-testid={`${testId}-action`}>
                {action.label}
              </Link>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const QueueSection = ({ category }) => (
  <section className="ops-queue-section" data-testid={category.testId}>
    <h3>
      {category.title}
      <span className="ops-section-count is-alert" data-testid={`${category.testId}-count`}>
        {categoryCount(category)}
      </span>
      {category.note && <em className="ops-section-note">{category.note}</em>}
    </h3>
    {category.rows ? (
      <QueueTable category={category} />
    ) : (
      <p className="ops-queue-callout" data-testid={`${category.testId}-callout`}>
        <span>{category.callout(category.count)}</span>
        <Link className="ops-row-action" to={category.link.to} data-testid={`${category.testId}-action`}>
          {category.link.label} <ArrowRight size={14} aria-hidden="true" />
        </Link>
      </p>
    )}
  </section>
);

export const AttentionQueue = ({ attention, coverage, clientScoped }) => {
  const categories = buildCategories(attention, coverage, clientScoped);
  const total = categories.reduce((sum, category) => sum + categoryCount(category), 0);
  const active = categories.filter((category) => categoryCount(category) > 0);
  return (
    <section className="ops-card ops-attention" data-testid="needs-attention-panel">
      <div className="ops-card-heading">
        <div>
          <div className="ops-attention-title">
            <h2>Needs attention</h2>
            <span className={`ops-count ops-count-lg ${total ? "is-alert" : "is-clear"}`} data-testid="needs-attention-total">
              {total}
            </span>
          </div>
          <p>
            {total
              ? "Evaluations, provider submissions and unlinked positions waiting on TAG"
              : "Beyond the staffing queue above, nothing is waiting on TAG right now"}
          </p>
        </div>
        <ul className="ops-attention-summary" aria-label="Attention categories">
          {categories.map((category) => {
            const count = categoryCount(category);
            return count ? (
              <li key={category.id} className="ops-chip is-alert" data-testid={`${category.testId}-chip`}>
                <strong>{count}</strong> {category.title}
              </li>
            ) : (
              <li key={category.id} className="ops-chip is-clear" data-testid={`${category.testId}-clear`}>
                <Check size={13} strokeWidth={2.5} aria-hidden="true" /> {category.title} · All clear
              </li>
            );
          })}
        </ul>
      </div>
      {active.length > 0 && active.map((category) => <QueueSection key={category.id} category={category} />)}
    </section>
  );
};
